import React, { useEffect, useState } from "react";
import MyPageBanner from "../components/MyPageBanner";
import { supabase } from "../supabaseClient";
import StateMessage from "../components/StateMessage";
import "./SuspensionsPage.css";
import { localDateString } from "../utils/dates";
import { isCurrentPlayerSuspension } from "../utils/suspensions";

const SuspensionsPage = ({ bannerImage }) => {
  const [suspensions, setSuspensions] = useState([]);
  const [fanSuspensions, setFanSuspensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const { data: players, error: playersErr } = await supabase
          .from("suspensions")
          .select(`
            id, 
            end_date, 
            suspension_type,
            games_suspended,
            player_teams!inner (
              players ( player_name ),
              teams!inner (
                team_name,
                leagues!inner ( league_name, theme_color )
              )
            )
          `)
          .eq("is_active", true)
          .eq("player_teams.teams.leagues.is_published", true);

        if (playersErr) throw playersErr;

        const { data: fans, error: fansErr } = await supabase
          .from("fan_suspensions")
          .select("id, fan_name, end_date, reason, is_active")
          .eq("is_active", true);

        if (fansErr) throw fansErr;

        const today = localDateString();

        const playersData = (players || [])
          .filter((s) => isCurrentPlayerSuspension(s, today))
          .map((row) => ({
            suspension_id: row.id,
            end_date: row.end_date,
            suspension_type: row.suspension_type,
            games_suspended: row.games_suspended,
            player_name: row.player_teams?.players?.player_name,
            team_name: row.player_teams?.teams?.team_name,
            league_name: row.player_teams?.teams?.leagues?.league_name,
            theme_color: row.player_teams?.teams?.leagues?.theme_color,
          }));

        const fansData = (fans || [])
          .filter((f) => !f.end_date || f.end_date >= today)
          .map((row) => ({
            suspension_id: row.id,
            person_name: row.fan_name,
            end_date: row.end_date,
            reason: row.reason,
          }));

        setSuspensions(playersData);
        setFanSuspensions(fansData);
      } catch (e) {
        console.error(e);
        setErr("No se pudieron cargar las suspensiones.");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  return (
    <>
      <MyPageBanner
        imageUrl={bannerImage}
        text="Suspensiones"
        subtitle="Jugadores y personas con una suspensión activa."
      />

      <div className="susp-page">
        <section className="susp-table" aria-labelledby="susp-players-title">
          <header className="susp-head">
            <h2 className="block-title" id="susp-players-title">
              Jugadores
            </h2>
            {!loading && !err && (
              <span className="susp-count">{suspensions.length}</span>
            )}
          </header>

          {loading && <SuspSkeleton />}
          {err && (
            <StateMessage kind="error" title={err}>
              Revisa tu conexión e intenta de nuevo.
            </StateMessage>
          )}
          {!loading && !err && suspensions.length === 0 && (
            <StateMessage plain>No hay jugadores suspendidos.</StateMessage>
          )}

          {!loading && !err && suspensions.length > 0 && (
            <ul className="susp-body">
              {suspensions.map((s) => (
                <li
                  key={s.suspension_id}
                  className="susp-row"
                  style={{
                    "--row-accent": s.theme_color || "var(--accent)",
                  }}
                >
                  <div className="susp-cell name">
                    <span className="susp-primary">{s.player_name}</span>
                    <span className="susp-secondary">
                      {[s.team_name, s.league_name].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  <div className="susp-cell end">
                    {s.suspension_type === "games" ? (
                      <>
                        <span className="susp-end-label">Restan</span>
                        <span className="susp-end-value">
                          {gamesLabel(s.games_suspended)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="susp-end-label">
                          {s.end_date ? "Hasta" : "Duración"}
                        </span>
                        <span className="susp-end-value">
                          {formatDate(s.end_date)}
                        </span>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="susp-table fans" aria-labelledby="susp-fans-title">
          <header className="susp-head">
            <h2 className="block-title" id="susp-fans-title">
              Aficionados y otras personas
            </h2>
            {!loading && !err && (
              <span className="susp-count">{fanSuspensions.length}</span>
            )}
          </header>

          {loading && <SuspSkeleton rows={1} />}
          {!loading && !err && fanSuspensions.length === 0 && (
            <StateMessage plain>No hay personas suspendidas.</StateMessage>
          )}

          {!loading && !err && fanSuspensions.length > 0 && (
            <ul className="susp-body">
              {fanSuspensions.map((f) => (
                <li key={f.suspension_id} className="susp-row fans">
                  <div className="susp-cell name">
                    <span className="susp-primary">{f.person_name}</span>
                    {f.reason && (
                      <span className="susp-secondary susp-reason">{f.reason}</span>
                    )}
                  </div>
                  <div className="susp-cell end">
                    <span className="susp-end-label">
                      {f.end_date ? "Hasta" : "Duración"}
                    </span>
                    <span className="susp-end-value">{formatDate(f.end_date)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
};

function SuspSkeleton({ rows = 2 }) {
  return (
    <div className="susp-body" role="status" aria-label="Cargando suspensiones…">
      {Array.from({ length: rows }).map((_, i) => (
        <span key={i} className="skeleton susp-skeleton" />
      ))}
    </div>
  );
}

function gamesLabel(n) {
  const count = Number(n) || 0;
  return `${count} ${count === 1 ? "partido" : "partidos"}`;
}

function formatDate(d) {
  if (!d) return "Indefinida";
  const date = d instanceof Date ? d : new Date(`${d}T00:00:00`);
  if (isNaN(date)) return d;
  return date.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default SuspensionsPage;
